import React, { createContext, useContext, useReducer, useCallback, useRef } from 'react';
import urlJoin from 'url-join';
import imageCompression from 'browser-image-compression';
import { isPlatform } from '@ionic/react';

import { useClassroomInterruption } from 'Util/Hooks';
import useClassroomVideoPlayer from 'Util/Hooks/useClassroomVideoPlayer';
import { secondsToTime } from 'Util/datetime';
import fetch2, { METHOD } from 'Util/fetch';
import { useVideoPlayer } from 'Context/VideoPlayer';
import { useConfig } from 'Context/ConfigContext';
import { useClassroomContext } from 'Context/ClassroomContext';

import useMyExternalProfile from '../../../../Domain/useMyExternalProfile';
import useSubsectionContent from '../../../../Domain/useSubsectionContent';

const OPTIMIZE_OPTIONS = {
  maxSizeMB: 2,
  maxWidthOrHeight: 1024,
  useWebWorker: true,
};

const ClearByLearnEdContext = createContext();

const SUBMISSION_STATE = {
  INITIALIZED: 'INITIALIZED',
  FILLING_FORM: 'FILLING_FORM',
  REVIEWING: 'REVIEWING',
  SUBMITTING: 'SUBMITTING',
  RE_SUBMITTING: 'RE_SUBMITTING',
  FAILED: 'FAILED',
  COMPLETED: 'COMPLETED',
};

const TICKET_TYPE = {
  IN_BOOK: 'IN_BOOK',
};

const INITIAL_STATE = {
  submissionState: SUBMISSION_STATE.INITIALIZED,
  ticket: null,
};

const ClearByLearnEdProvider = ({ children }) => {
  const [state, setState] = useReducer(
    (state, newState) =>
      typeof newState === 'function' ? newState(state) : { ...state, ...newState },
    INITIAL_STATE
  );
  const { classroomCourseId, currentSubsectionId, currentSubsectionReferId } =
    useClassroomContext();
  const { isInterrupting, activateInterruption, deactivateInterruption, courseData } =
    useClassroomInterruption();
  const { playerRef } = useVideoPlayer();
  const { getCurrentTime } = useClassroomVideoPlayer();

  const { userApi, classroom: classroomConfig } = useConfig();
  const dashboardURL = classroomConfig?.clearByLearnEd?.dashboardURL;
  const createTicketApi = classroomConfig?.clearByLearnEd?.createTicketApi;

  const { data } = useMyExternalProfile({ fetchPolicy: 'cache-only' });
  const { data: subsectionContentData } = useSubsectionContent({
    variables: {
      classroomCourseId,
      subSectionId: currentSubsectionId,
      subSectionReferId: currentSubsectionReferId,
    },
    fetchPolicy: 'cache-only',
  });

  const contentProvider =
    subsectionContentData?.classroomSubSectionContent?.assets?.contentAssets?.[0]?.content
      ?.provider;

  // Enable screenshot when provider isn't YOUTUBE ,or provider is BYTEARK and the platform is iOS
  const enableScreenshot = !(
    contentProvider === 'YOUTUBE' ||
    (isPlatform('ios') && contentProvider !== 'BYTEARK' && contentProvider !== 'LEARNCRAFT')
  )
    ? classroomConfig?.clearByLearnEd?.enableScreenshot
    : false;

  const ticketRef = useRef();

  const goToFillingFormStep = useCallback(
    (ticket = {}, { onGoToFillingFormStep, onGoToFillingFormStepCompleted } = {}) => {
      onGoToFillingFormStep?.();
      activateInterruption();
      const currentTimeSecond = getCurrentTime();
      const timeSnapshot = secondsToTime(currentTimeSecond ?? 0);
      const subsectionContent = subsectionContentData?.classroomSubSectionContent;

      setState((prev) => {
        const updatedTicket = {
          type: TICKET_TYPE.IN_BOOK,
          courseName: courseData?.courseTitle,
          courseCode: courseData?.courseCode,
          videoTitle: playerRef?.props?.src?.title ?? subsectionContent?.title,
          videoTime: timeSnapshot,
          ...prev.ticket,
          ...ticket,
        };

        ticketRef.current = updatedTicket;
        return {
          submissionState: SUBMISSION_STATE.FILLING_FORM,
          ticket: updatedTicket,
        };
      });
      onGoToFillingFormStepCompleted?.();
    },
    [activateInterruption, courseData, subsectionContentData, playerRef]
  );

  const goToReviewingStep = useCallback(
    (formData, { onGoToReviewingStep, onGoToReviewingStepCompleted } = {}) => {
      onGoToReviewingStep?.();
      setState((prev) => {
        const updatedTicket = { ...prev.ticket, ...formData };
        ticketRef.current = updatedTicket;
        return {
          submissionState: SUBMISSION_STATE.REVIEWING,
          ticket: updatedTicket,
        };
      });
      onGoToReviewingStepCompleted?.();
    },
    []
  );

  const getToken = useCallback(async () => {
    const providerName = data?.myExternalProfile?.providerName;

    return await fetch2(
      urlJoin(userApi, `/me/externalToken?providerName=${providerName}`),
      null,
      METHOD.GET,
      null,
      {
        isRedirect: false,
        includeCredentials: true,
      }
    );
  }, [data?.myExternalProfile?.providerName, userApi]);

  const submitTicket = useCallback(
    async ({ onCompleted, onError }) => {
      setState((prev) => ({
        ...prev,
        submissionState:
          prev?.submissionState === SUBMISSION_STATE.FAILED
            ? SUBMISSION_STATE.RE_SUBMITTING
            : SUBMISSION_STATE.SUBMITTING,
      }));

      try {
        const token = await getToken();
        const header = new Headers();
        header.append('Authorization', `Bearer ${token}`);

        const _ticket = ticketRef.current;
        const formData = new FormData();
        const imageFilesWithScreenshot = _ticket.screenshot
          ? [_ticket.screenshot, ..._ticket.images]
          : _ticket.images;

        formData.append('crs_code', _ticket.courseCode);
        formData.append('tick_type', _ticket.type);
        formData.append('tickm_message', _ticket.question);

        let compressedFiles = imageFilesWithScreenshot
          .filter((image) => image.file)
          .map((image) => image.file);
        const compressionPromises = imageFilesWithScreenshot.map((image) =>
          imageCompression(image.file, OPTIMIZE_OPTIONS)
        );

        try {
          compressedFiles = await Promise.all(compressionPromises);
        } catch (error) {
          console.error(error);
        }
        compressedFiles.map((compressedFile) => formData.append('files[]', compressedFile));

        const requestOptions = {
          method: METHOD.POST,
          headers: header,
          body: formData,
        };

        const response = await fetch(createTicketApi, requestOptions);
        const responseBody = await response.json();

        const statusCode = responseBody?.code;
        if (statusCode) throw new Error(responseBody);

        const ticketId = responseBody?.tick_id;
        if (!ticketId) throw new Error('ticketId not found');

        ticketRef.current.ticketId = ticketId;
        onCompleted?.();
      } catch (error) {
        console.error(error);
        onError?.();
        setState({ submissionState: SUBMISSION_STATE.FAILED });
      }
    },
    [getToken, createTicketApi]
  );

  const goToCompletedStep = useCallback(
    async ({ onGoToCompletedStep, onGoToCompletedStepCompleted } = {}) => {
      onGoToCompletedStep?.();
      await submitTicket({
        onCompleted: () => setState({ submissionState: SUBMISSION_STATE.COMPLETED }),
      });
      onGoToCompletedStepCompleted?.();
    },
    [submitTicket]
  );

  const setAttachment = useCallback((updatingFiles = []) => {
    // Update custom image file
    setState((prev) => {
      const updatedTicket = {
        ...prev.ticket,
        images: [...updatingFiles],
      };

      ticketRef.current = updatedTicket;
      return { ...prev, ticket: updatedTicket };
    });
  }, []);

  const reset = useCallback(
    ({ onReset, onResetCompleted } = {}) => {
      onReset?.();
      ticketRef.current = null;
      deactivateInterruption();
      setState(INITIAL_STATE);
      onResetCompleted?.();
    },
    [deactivateInterruption]
  );

  const getClearUrlLink = useCallback(async () => {
    const ticketId = ticketRef.current.ticketId;
    if (!ticketId) return null;
    const token = await getToken();
    const clearUrlLink = dashboardURL.replace('{token}', token);
    return clearUrlLink;
  }, [getToken, dashboardURL]);

  return (
    <ClearByLearnEdContext.Provider
      value={[
        state.submissionState,
        state.ticket,
        {
          goToFillingFormStep,
          goToReviewingStep,
          goToCompletedStep,
          reset,
          setAttachment,
          getClearUrlLink,
          submitTicket,
        },
        ticketRef.current,
        { enableScreenshot },
      ]}
    >
      {children}
    </ClearByLearnEdContext.Provider>
  );
};

const useClearByLearnEd = () => {
  const context = useContext(ClearByLearnEdContext);
  if (context === undefined) {
    throw new Error('useClearByLearnEd must be used within ClearByLearnEdProvider');
  }
  return context;
};

export { useClearByLearnEd, ClearByLearnEdProvider, SUBMISSION_STATE };
export default ClearByLearnEdProvider;
